/** Session helpers — converts step answers to API payloads and maps server results to local types. */
import type { SubmitResponseResultFull } from '@formency/shared';
import type { StepSubmitResponse } from '@/shared/api/hooks/useStepSubmit';
import type { StepAnswer, StepResult } from './types';

// ─── Session helpers ───────────────────────────────────────────────────────────

/**
 * Convert a session StepAnswer into the `responseJson` shape the API expects.
 *
 * The API expects raw values — NOT wrapped in { type, ... } discriminated unions.
 * MCQ/ranking → string[], true_false → "true"/"false", text → string,
 * cloze/classify → Record<string,string>.
 */
export function answerToResponseJson(answer: StepAnswer): unknown {
  switch (answer.type) {
    case 'mcq':
      return answer.selectedIds;
    case 'true_false':
      return String(answer.selected);
    case 'text':
      return answer.value;
    case 'cloze':
      return answer.blanks;
    case 'cloze_diagram':
      return answer.blanks;
    case 'cloze_image':
      return answer.blanks;
    case 'ranking':
      return answer.order;
    case 'classify':
      return answer.assignments;
    case 'image_hotspot':
      return { x: answer.x, y: answer.y };
    case 'numeric':
      return { value: answer.value, ...(answer.unit !== undefined && { unit: answer.unit }) };
    case 'info':
      return {};
  }
}

/**
 * Extract a StepResult from the API's full submit response.
 */
export function toStepResult(full: SubmitResponseResultFull): StepResult {
  return {
    isCorrect: full.isCorrect,
    score: full.score,
    maxScore: full.maxScore,
    explanation: full.explanation,
    feedback: full.feedback ?? null,
    ...(full.correctChoiceIds && { correctChoiceIds: full.correctChoiceIds }),
    ...(full.blankResults && { blankResults: full.blankResults }),
    ...(full.rankingResults && { rankingResults: full.rankingResults }),
    ...(full.classifyResults && { classifyResults: full.classifyResults }),
    ...(full.hotspotResult && { hotspotResult: full.hotspotResult }),
    ...(full.numericResult && { numericResult: full.numericResult }),
  };
}

/**
 * Extract a StepResult from the new unified /submit endpoint response.
 * Handles both info steps (synthetic result) and question steps (full grading).
 */
export function toUnifiedStepResult(response: StepSubmitResponse): StepResult {
  if (response.stepKind === 'info') {
    return {
      isCorrect: true,
      score: 0,
      maxScore: 0,
      explanation: null,
      feedback: null,
    };
  }

  return {
    isCorrect: response.isCorrect ?? false,
    score: response.score ?? 0,
    maxScore: response.maxScore ?? 0,
    explanation: response.explanation ?? null,
    feedback: response.feedback ?? null,
    ...(response.correctChoiceIds && { correctChoiceIds: response.correctChoiceIds }),
    ...(response.blankResults && { blankResults: response.blankResults }),
    ...(response.rankingResults && { rankingResults: response.rankingResults }),
    ...(response.classifyResults && { classifyResults: response.classifyResults }),
    ...(response.hotspotResult && { hotspotResult: response.hotspotResult }),
    ...(response.numericResult && { numericResult: response.numericResult }),
  };
}

// ─── Grading outcome ──────────────────────────────────────────────────────────

/** Tri-state grading outcome derived from a StepResult. */
export type GradingOutcome = 'correct' | 'partial' | 'incorrect';

/**
 * Determines the grading outcome from a step result.
 *
 * - `'correct'`   — isCorrect is true (all answers right)
 * - `'partial'`   — isCorrect is false but score > 0 (some answers right, cloze/classify only)
 * - `'incorrect'` — isCorrect is false and score is 0 (all wrong)
 */
export function getGradingOutcome(result: StepResult): GradingOutcome {
  if (result.isCorrect) return 'correct';
  if (result.score > 0) return 'partial';
  return 'incorrect';
}

/** Outcome-keyed background + text color for explanation/feedback boxes. */
export const OUTCOME_COLORS = {
  correct: {
    background: 'var(--color-success-subtle, var(--f-success-subtle))',
    color: 'var(--color-success-text, var(--f-success-text))',
  },
  partial: {
    background: 'var(--color-warning-subtle, var(--f-warning-subtle))',
    color: 'var(--color-warning-text, var(--f-warning-text))',
  },
  incorrect: {
    background: 'var(--color-error-subtle, var(--f-error-subtle))',
    color: 'var(--color-error-text, var(--f-error-text))',
  },
} as const;

// ─── Completion metrics ───────────────────────────────────────────────────────

interface ScoredStep {
  stepKind: string;
  score: number;
  maxScore: number;
}

/**
 * Calculate completion metrics from answered steps, excluding info steps from scoring.
 * Used by SessionPlayer and tested directly in sessionUtils.test.ts.
 */
export function calculateCompletionMetrics(steps: ScoredStep[]) {
  let totalScore = 0;
  let totalMaxScore = 0;
  let questionsCorrect = 0;
  let questionsTotal = 0;

  for (const step of steps) {
    if (step.stepKind === 'info') continue;
    totalScore += step.score;
    totalMaxScore += step.maxScore;
    questionsTotal += 1;
    if (step.score > 0 && step.score >= step.maxScore) {
      questionsCorrect += 1;
    }
  }

  const scorePct = totalMaxScore > 0 ? (totalScore / totalMaxScore) * 100 : 0;
  const completionPct = steps.length > 0 ? (steps.length / steps.length) * 100 : 0;

  return {
    scoreRaw: totalScore,
    scorePct,
    completionPct,
    questionsCorrect,
    questionsTotal,
    totalMaxScore,
    passed: null,
    stepsCompleted: steps.length,
  };
}
